import Backbone from "backbone";
import config from "./config";
import Constants from "./helpers/Constants";
import HydrateModelData from "./helpers/HydrateModelData";
import LiveQueryBanner from "./helpers/LiveQueryBanner";
import LiveQueryMonitor from "./helpers/LiveQueryMonitor";
import Navigation from "./helpers/Navigation";
import ObservingList from "./helpers/ObservingList";
import ParseSdk from "./helpers/ParseSdk";
import SkyObjectIcon from "./helpers/SkyObjectIcon";
import SkyObjectObservationHelper from "./helpers/SkyObjectObservation";
import UI from "./helpers/UI";
import Vendor from "./helpers/Vendor";
import AccessModel from "./models/Access";
import AstrobinImageOfTheDay from "./models/AstrobinImageOfTheDay";
import EquipmentModel from "./models/Equipment";
import FeaturedStoryModel from "./models/FeaturedStory";
import FOVIndicatorModel from "./models/FOVIndicator";
import ObservingListModel from "./models/ObservingList";
import ObservingListAuxModel from "./models/ObservingListAux";
import ObservingSessionModel from "./models/ObservingSession";
import ObservingSiteModel from "./models/ObservingSite";
import ObservingStatusStatisticsModel from "./models/ObservingStatusStatistics";
import SavedSettingModel from "./models/SavedSetting";
import SkyObjectObservationModel from "./models/SkyObjectObservation";
import StaticEquipmentModel from "./models/StaticEquipment";
import StaticObservingListModel from "./models/StaticObservingList";
import UserModel from "./models/User";
import BaseView from "./views/BaseView";

window.Portal = {
  Collections: {},
  Routers: {},
  Current: {
    Collections: {},
  },
};

const modelsToHydrate = [
  {
    model: EquipmentModel,
    collectionName: "equipments",
    query: {
      order: "descending", // default
      orderOn: "updatedAt", // default
    },
  },
  {
    model: FOVIndicatorModel,
    collectionName: "fovIndicators",
    query: {
      order: "ascending",
      orderOn: "sortOrder",
    },
  },
  {
    model: ObservingSessionModel,
    collectionName: "observingSessions",
    query: {
      order: "descending",
      orderOn: "startJD",
    },
  },
  {
    model: ObservingSiteModel,
    collectionName: "observingSites",
  },
  {
    model: SavedSettingModel,
    collectionName: "savedSettings",
    query: {
      order: "ascending",
      orderOn: "sortOrder",
    },
  },
  {
    model: ObservingListModel,
    collectionName: "observingLists",
    query: {
      order: "ascending",
      orderOn: "sortOrder",
    },
  },
  {
    model: ObservingListAuxModel,
    collectionName: "observingListAuxs",
  },
  {
    model: StaticObservingListModel,
    collectionName: "staticObservingLists",
    query: {
      order: "ascending",
      orderOn: "title",
      global: true,
    },
  },
  {
    model: SkyObjectObservationModel,
    collectionName: "skyObjectObservations",
    query: {
      order: "descending",
      orderOn: "beginJD",
      limit: 100, // Reduced - list view now uses server-side pagination
      includes: ["session", "observingList"],
    },
  },
  {
    model: StaticEquipmentModel,
    collectionName: "staticEquipments",
    query: {
      order: "ascending",
      orderOn: "name",
      global: true,
    },
  },
  {
    model: AstrobinImageOfTheDay,
    collectionName: "astrobinImageOfTheDay",
    query: {
      order: "descending",
      orderOn: "image_of_day_date",
      global: true,
    },
  },
];

const initialize = async () => {
  // initialize parse js sdk
  try {
    // Add BaseView to Portal namespace before any views are imported
    ParseSdk.init();

    if (ParseSdk.checkLogin()) {
      LiveQueryMonitor.init();
      HydrateModelData.initRecovery();
      LiveQueryBanner.init();
      // Initialize Stripe
      Portal.Current.api = {
        createCheckout: config.stripe.createCheckout,
        plans: config.stripe.plans,
        customers: config.stripe.customers,
        subscriptions: config.stripe.subscriptions,
        coupons: config.stripe.coupons,
        stripePublicKey: config.stripe.publicKey,
        MONTHLY_PLAN_ID: config.stripe.monthlyPlanId,
        YEARLY_PLAN_ID: config.stripe.yearlyPlanId,
      };
      // store the current user and set access
      Portal.Current.user = UserModel;

      // Listen for access initialization to handle side effects
      Backbone.on("access:initialized", async (state) => {
        // Set clientApps on user model
        Portal.Current.user.clientApps = state.clientApps;
        // Update premium UI elements
        UI.updateAccessElements(state.flags.hasPremium);
        // Fetch Stripe IDs if subscription is via Stripe
        if (state.subscriptionStore === "stripe") {
          const user = await AccessModel.getUser();
          const stripeData = await AccessModel.fetchStripeIds(user);
          if (stripeData) {
            Portal.Current.user.subscriptionID = stripeData.subscriptionId;
            Portal.Current.user.stripeID = stripeData.customerId;
            Portal.Current.user.trial_not_available = stripeData.trialNotAvailable;
          }
        }
      });

      Portal.Current.access = await AccessModel.init();
      Navigation.initNavEvents();
      // DataTable defaults
      await Vendor.initDataTables();
      // load Model data into collections and set event listeners
      const promisesToHydrate = modelsToHydrate.map((model) => {
        return HydrateModelData.hydrate(model);
      });

      await Promise.all([
        ...promisesToHydrate,
        ParseSdk.loadConfig(),
        SkyObjectIcon.hydrateIconCollection(),
        HydrateModelData.populateFeaturedArticleCollection(),
      ]);

      // load all ObservingListAux data into their respective ObservingList
      ObservingList.setAuxLists();
      UI.listenForFullscreen();
      UI.setupRemoteModals();
      UI.setupDates();

      // initialize a router - import dynamically after Portal is initialized
      const { default: PortalRouter } = await import("./routers/PortalRouter");
      const portalRouter = new PortalRouter();
      portalRouter.start(Portal.Current.Collections);

      // Navigation update handler
      const updateNavigation = () => {
        const url = window.location.hash;
        url !== "" && url !== "#/"
          ? Navigation.setUpUrl(url)
          : Navigation.setUpUrl("default");
        gtag("config", config.analytics.gaTrackingId, {
          page_path: url.replace("#/", ""),
        });
      };

      // Attach route listener BEFORE starting history
      Backbone.history.on("route", updateNavigation);
      // start listening for routes
      Backbone.history.start();
      // Update navigation for initial route (listener misses first route)
      updateNavigation();
      // Show content after router renders initial view
      UI.showContent();
    }
  } catch (error) {
    console.error("Error initializing Parse SDK: ", error);
    UI.showParseError();
  }
};

export { initialize };
